import { Notification } from "@/utils/notificationUtils";
import { openBlobFile } from "@/utils/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import t from "../../../app/i18n";
import { clientApi } from "../../../modules/client/api";
import {
  ClientFilterExportRequest,
  ClientFilterPageRequest,
  ClientSearchRequest,
  CreateUpdateClient,
  MarkClientAttachmentType,
  UpdateClientAssignedAgents,
  ValidateContractClientRequest,
  ValidateContractClientsRequest,
} from "../../../modules/client/types";
import {
  AgentCreateUserWithClientUserAccount,
  AgentUpdateClientUserAccountWithUser,
} from "../../../modules/user/types";
import { UUID } from "../../../typings/global";
import { EntityObject, ThreeLevelEntityIdObject, TwoLevelEntityIdObject, TwoLevelEntityObject } from "../../types";
import { CONTRACTS_QUERY_KEYS } from "./contract/contracts";

export const CLIENT_QUERY_KEYS = {
  filterClients: (payload?: ClientFilterPageRequest) => (payload ? ["filterClients", payload] : ["filterClients"]),
  importClients: () => ["importClients"],
  downloadClientAttachmentsAsZip: () => ["downloadClientAttachmentsAsZip"],
  downloadClientsExport: () => ["downloadClientsExport"],
  getClient: (id: UUID) => ["getClient", id],
  createClient: () => ["createClient"],
  updateClient: () => ["updateClient"],
  updateClientAssignedAgents: () => ["updateClientAssignedAgents"],
  deleteClient: () => ["deleteClient"],
  getClientPartnerAgent: (id: UUID) => ["getClientPartnerAgent", id],
  getClientAttachments: (id: UUID) => ["getClientAttachments", id],
  uploadClientAttachments: () => ["uploadClientAttachments"],
  deleteClientAttachment: () => ["deleteClientAttachment"],
  markClientAttachmentType: () => ["markClientAttachmentType"],
  getClientUserAccounts: (id?: UUID) => (id ? ["getClientUserAccounts", id] : ["getClientUserAccounts"]),
  createUserWithClientUserAccount: () => ["createUserWithClientUserAccount"],
  resendUserConfirmLink: () => ["resendUserConfirmLink"],
  updateClientUserAccount: () => ["updateClientUserAccount"],
  deleteClientUserAccount: () => ["deleteClientUserAccount"],
  searchClient: () => ["searchClient"],
  validateClient: () => ["validateClient"],
  validateClients: () => ["validateClients"],
  assignAttachmentToContract: () => ["assignAttachmentToContract"],
};

export const useFilterClients = (payload: ClientFilterPageRequest) => {
  return useQuery({
    queryKey: CLIENT_QUERY_KEYS.filterClients(payload),
    queryFn: async () => {
      const { data } = await clientApi.filterClients(payload);

      return data;
    },
  });
};

export const useImportClients = () => {
  return useMutation({
    mutationKey: CLIENT_QUERY_KEYS.importClients(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: FormData) => {
      const response = await clientApi.importClients(payload);

      return response.data;
    },
    onSuccess: (data) => {
      Notification.getNotification().success({
        title: t("common.operationSuccess"),
        description: t("client.helpers.import.processStarted", { count: data }),
      });
    },
  });
};

export const useDownloadClientAttachmentsAsZip = () => {
  return useMutation({
    mutationKey: CLIENT_QUERY_KEYS.downloadClientAttachmentsAsZip(),
    mutationFn: async (id: UUID) => {
      const response = await clientApi.downloadClientAttachmentsAsZip({ id });
      openBlobFile(response, true);

      return response.data;
    },
  });
};

export const useDownloadClientsExport = () => {
  return useMutation({
    mutationKey: CLIENT_QUERY_KEYS.downloadClientsExport(),
    mutationFn: async (payload: ClientFilterExportRequest) => {
      const response = await clientApi.downloadClientsExport(payload);
      openBlobFile(response, true);

      return response.data;
    },
  });
};

export const useGetClient = (id: UUID) => {
  return useQuery({
    queryKey: CLIENT_QUERY_KEYS.getClient(id),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await clientApi.getClient({ id });

      return data;
    },
  });
};

export const useGetClientPartnerAgent = (id: UUID, enabled?: boolean) => {
  return useQuery({
    queryKey: CLIENT_QUERY_KEYS.getClientPartnerAgent(id),
    queryFn: async () => {
      const { data } = await clientApi.getClientPartnerAgent({ id });

      return data;
    },
    enabled,
  });
};

export const useCreateClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CLIENT_QUERY_KEYS.createClient(),
    mutationFn: async (payload: CreateUpdateClient) => {
      const { data } = await clientApi.createClient(payload);

      return data;
    },
    onSuccess: () => {
      Notification.itemCreatedNotification();
      queryClient.invalidateQueries({ queryKey: CLIENT_QUERY_KEYS.filterClients() });
    },
  });
};

export const useUpdateClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CLIENT_QUERY_KEYS.updateClient(),
    mutationFn: async (payload: EntityObject<CreateUpdateClient>) => {
      const { data } = await clientApi.updateClient(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      Notification.itemUpdatedNotification();
      queryClient.invalidateQueries({ queryKey: CLIENT_QUERY_KEYS.filterClients() });
      queryClient.invalidateQueries({ queryKey: CLIENT_QUERY_KEYS.getClient(mutatedPayload.id) });
      queryClient.invalidateQueries({ queryKey: CLIENT_QUERY_KEYS.getClientPartnerAgent(mutatedPayload.id) });
    },
  });
};

export const useUpdateClientAssignedAgents = ({ globalSpinner = true } = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CLIENT_QUERY_KEYS.updateClientAssignedAgents(),
    meta: { globalSpinner },
    mutationFn: async (payload: EntityObject<UpdateClientAssignedAgents>) => {
      const { data } = await clientApi.updateClientAssignedAgents(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: CLIENT_QUERY_KEYS.getClient(mutatedPayload.id) });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteClient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CLIENT_QUERY_KEYS.deleteClient(),
    mutationFn: (id: UUID) => {
      return clientApi.deleteClient({ id });
    },
    onSuccess: (_, mutatedPayload) => {
      Notification.itemDeletedNotification();
      queryClient.invalidateQueries({ queryKey: CLIENT_QUERY_KEYS.filterClients() });
      queryClient.removeQueries({ queryKey: CLIENT_QUERY_KEYS.getClient(mutatedPayload) });
      queryClient.removeQueries({ queryKey: CLIENT_QUERY_KEYS.getClientPartnerAgent(mutatedPayload) });
    },
  });
};

export const useGetClientAttachments = (id: UUID, enabled?: boolean) => {
  return useQuery({
    queryKey: CLIENT_QUERY_KEYS.getClientAttachments(id),
    queryFn: async () => {
      const { data } = await clientApi.getClientAttachments({ id });

      return data;
    },
    enabled,
  });
};

export const useUploadClientAttachments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CLIENT_QUERY_KEYS.uploadClientAttachments(),
    mutationFn: async (payload: EntityObject<FormData>) => {
      const { data } = await clientApi.uploadClientAttachments(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(CLIENT_QUERY_KEYS.getClientAttachments(mutatedPayload.id), () => data);
    },
  });
};

export const useDeleteClientAttachment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CLIENT_QUERY_KEYS.deleteClientAttachment(),
    mutationFn: (payload: TwoLevelEntityIdObject) => {
      return clientApi.deleteClientAttachment(payload);
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: CLIENT_QUERY_KEYS.getClientAttachments(mutatedPayload.id1) });
    },
  });
};

export const useMarkClientAttachmentType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CLIENT_QUERY_KEYS.markClientAttachmentType(),
    mutationFn: async (payload: TwoLevelEntityObject<MarkClientAttachmentType>) => {
      const { data } = await clientApi.markClientAttachmentType(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: CLIENT_QUERY_KEYS.getClientAttachments(mutatedPayload.id1) });
    },
  });
};

export const useGetClientUserAccounts = (id?: UUID) => {
  return useQuery({
    queryKey: CLIENT_QUERY_KEYS.getClientUserAccounts(id),
    queryFn: async () => {
      const { data } = await clientApi.getClientUserAccounts({ id: id! });

      return data;
    },
    enabled: !!id,
  });
};

export const useCreateUserWithClientUserAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CLIENT_QUERY_KEYS.createUserWithClientUserAccount(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<AgentCreateUserWithClientUserAccount>) => {
      const { data } = await clientApi.createUserWithClientUserAccount(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLIENT_QUERY_KEYS.getClientUserAccounts() });
      Notification.itemCreatedNotification();
    },
  });
};

export const useResendUserConfirmLink = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CLIENT_QUERY_KEYS.resendUserConfirmLink(),
    mutationFn: async (payload: TwoLevelEntityIdObject) => {
      const { data } = await clientApi.resendUserConfirmLink(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLIENT_QUERY_KEYS.getClientUserAccounts() });

      Notification.getNotification().success({
        title: t("common.operationSuccess"),
        description: t("user.helpers.resendConfirmationLinkSuccess"),
      });
    },
  });
};

export const useUpdateClientUserAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CLIENT_QUERY_KEYS.updateClientUserAccount(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: TwoLevelEntityObject<AgentUpdateClientUserAccountWithUser>) => {
      const { data } = await clientApi.updateClientUserAccount(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLIENT_QUERY_KEYS.getClientUserAccounts() });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteClientUserAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CLIENT_QUERY_KEYS.deleteClientUserAccount(),
    mutationFn: (payload: TwoLevelEntityIdObject) => {
      return clientApi.deleteClientUserAccount(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CLIENT_QUERY_KEYS.getClientUserAccounts() });
      Notification.itemDeletedNotification();
    },
  });
};

export const useSearchClient = () => {
  return useMutation({
    mutationKey: CLIENT_QUERY_KEYS.searchClient(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: ClientSearchRequest) => {
      const { data } = await clientApi.searchClient(payload);

      return data;
    },
    // The store keeps the result; the cache would only hold on to the client's personal data.
    gcTime: 0,
    networkMode: "always",
  });
};

export const useValidateClient = () => {
  return useMutation({
    mutationKey: CLIENT_QUERY_KEYS.validateClient(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: ValidateContractClientRequest) => {
      const { data } = await clientApi.validateClient(payload);

      return data;
    },
  });
};

export const useValidateClients = () => {
  return useMutation({
    mutationKey: CLIENT_QUERY_KEYS.validateClients(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: ValidateContractClientsRequest) => {
      const { data } = await clientApi.validateClients(payload);

      return data;
    },
  });
};

export const useAssignClientAttachmentToContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CLIENT_QUERY_KEYS.assignAttachmentToContract(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: ThreeLevelEntityIdObject) => {
      const { data } = await clientApi.assignAttachmentToContract(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: CONTRACTS_QUERY_KEYS.getContract(mutatedPayload.id3) });
      Notification.itemUpdatedNotification();
    },
  });
};
